import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, ArrowRight, CalendarHeart } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: BACKEND_URL + "/api" });
const NAV_ITEMS = [
  { to: "/", label: "Beranda", exact: true, testid: "nav-home" },
  { to: "/paket-wedding-dwg", label: "Paket Wedding DWG", testid: "nav-paket-dwg" },
  { to: "/vendor", label: "Vendor & Harga", testid: "nav-vendor" },
];

export default function PublicNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoUrl, setLogoUrl] = useState("/brand/monogram-dwg.svg");
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    publicApi.get("/public/cms/landing")
      .then(({ data }) => { if (data?.logo_image_url) setLogoUrl(`${BACKEND_URL}${data.logo_image_url}`); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header className={`zone-dark sticky top-0 z-50 border-b transition-[padding,box-shadow,border-color] duration-500 ${
        scrolled ? "border-[var(--dark-line)] shadow-[0_10px_30px_-18px_rgba(0,0,0,0.6)]" : "border-transparent"}`} data-testid="public-nav">
        <div className={`max-w-7xl mx-auto px-3 sm:px-6 flex items-center justify-between gap-2 md:gap-4 transition-[padding] duration-500 ${scrolled ? "py-2" : "py-3 md:py-4"}`}>
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group" data-testid="public-nav-logo">
            <img src={logoUrl} alt="Logo Dessy Wedding Group"
              className={`rounded-full object-cover transition-[width,height,transform] duration-500 group-hover:rotate-[8deg] ${scrolled ? "w-8 h-8 md:w-9 md:h-9" : "w-9 h-9 md:w-10 md:h-10"}`}
              data-testid="public-nav-logo-custom" />
            <div className="leading-tight">
              <div className={`font-serif font-semibold tracking-wide text-[var(--dark-text)] transition-[font-size] duration-500 ${scrolled ? "text-[14px] md:text-[17px]" : "text-[15px] md:text-[19px]"}`}>
                Dessy Wedding Group
              </div>
              <div className="text-[var(--champagne)] uppercase tracking-[0.2em] text-[9px] font-semibold">Est. 2015</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.exact} data-testid={it.testid}
                className={({ isActive }) => `px-3 py-2 text-[13px] font-medium relative transition-colors ${isActive ? "text-[var(--dark-text)]" : "text-[var(--dark-muted)] hover:text-[var(--dark-text)]"}`}>
                {({ isActive }) => (
                  <>
                    {it.label}
                    <span className={`absolute -bottom-0.5 left-3 right-3 h-px bg-[var(--champagne)] origin-left transition-transform duration-300 ${isActive ? "scale-x-100" : "scale-x-0"}`} />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <Link to="/consultation" className="btn btn-dark-primary !min-h-[40px] !px-3 md:!px-5 whitespace-nowrap group" data-testid="nav-consultation">
              <CalendarHeart className="w-4 h-4" />
              <span className="hidden sm:inline">Konsultasi Gratis</span>
              <span className="sm:hidden">Konsul</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform hidden md:inline" />
            </Link>
            <button onClick={() => setOpen(true)} aria-label="Buka menu navigasi" data-testid="mobile-menu-open"
              className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-[var(--champagne)] text-[var(--dark-text)] rounded-[6px]">
              <MenuIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="mobile-menu-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="zone-dark absolute right-0 top-0 h-full w-[85vw] max-w-sm flex flex-col animate-slide-in">
            <style>{`@keyframes slideIn { from { transform: translateX(100%);} to { transform: translateX(0);} } .animate-slide-in { animation: slideIn 0.28s ease-out; }`}</style>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--dark-line)]">
              <div className="flex items-center gap-2">
                <img src={logoUrl} alt="Logo Dessy Wedding Group" className="w-8 h-8 rounded-full object-cover" />
                <div className="font-serif font-semibold text-[16px]">Menu</div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Tutup menu" data-testid="mobile-menu-close"
                className="inline-flex items-center gap-1.5 border border-[var(--dark-line)] text-[var(--dark-muted)] px-3 py-2 rounded-[6px] text-[12px] font-medium">
                <X className="w-3.5 h-3.5" /> Tutup
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-2">
              {[...NAV_ITEMS, { to: "/portal", label: "Portal Klien", testid: "nav-portal" }].map((it) => (
                <NavLink key={it.to} to={it.to} end={it.exact} onClick={() => setOpen(false)} data-testid={`m-${it.testid}`}
                  className={({ isActive }) => `flex items-center justify-between px-6 py-4 border-b border-[var(--dark-line)] text-[15px] font-medium transition-colors ${isActive ? "text-[var(--rose)]" : "text-[var(--dark-text)] hover:bg-white/5"}`}>
                  <span>{it.label}</span>
                  <ArrowRight className="w-4 h-4 opacity-60" />
                </NavLink>
              ))}
            </nav>
            <div className="p-5 border-t border-[var(--dark-line)]">
              <Link to="/consultation" onClick={() => setOpen(false)} className="btn btn-dark-primary w-full" data-testid="m-nav-consultation-cta">
                <CalendarHeart className="w-4 h-4" /> Konsultasi Gratis <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <div className="text-[11px] text-[var(--dark-muted)] mt-3 text-center">Gratis · Respons 24 jam</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
